import { useSessionStorage } from "@vueuse/core";
import { computed } from "vue";
import { BRL_CURRENCY_CODE, isBrlCurrencyDisabled } from "@/core/utilities/brlCurrency";
import { punchoutCookies } from "@/core/utilities/punchout/punchoutCookies";
import { useUser } from "@/shared/account/composables/useUser";
import { INDIA_SUPPORTED_CURRENCIES } from "@/shared/checkout/constants";
import { countryCodeToLanguageAndCurrencyMap } from "../constants/language-and-currency";
import FeatureFlags from "../utilities/featureFlags";
import { useThemeContext } from "./useThemeContext";
import type { ICurrency } from "../types";
import type { LanguageAndCurrency } from "../types/defaultLanguageCurrency";

const { themeContext } = useThemeContext();
const { user, isAuthenticated } = useUser();

// Seed sessionStorage from localStorage on init so new tabs inherit the last-selected
// currency. Each tab then maintains its own independent copy, eliminating cross-tab
// contamination via StorageEvent (sessionStorage writes are never broadcast to other tabs).
const savedCurrencyCode = useSessionStorage<string | null>("currency", localStorage.getItem("currency"));

const defaultCurrency = computed<ICurrency>(() => themeContext.value.defaultCurrency);

// India project: INR (and the India checkout rules) are only offered to signed-in users; guests never see INR.
// While the "EnableInrCurrency" store setting is on, every signed-in user gets INR. While it is off, only contacts
// marked "INR Allowlisted" in the back office do, so pre-GA testers can use INR on production without exposing it
// to the public. Mirrors InrCurrencyAvailabilityService in the Commerce module (a missing setting counts as off).
const isInrCurrencyEnabled = computed<boolean>(() => {
  if (!isAuthenticated.value) {
    return false;
  }

  const modules = themeContext.value.storeSettings?.modules ?? [];
  const isInrFlagOn = new FeatureFlags(modules).isFeatureEnabled("Thorlabs.Commerce", "EnableInrCurrency");

  return isInrFlagOn || user.value?.contact?.isInrAllowlistedTL === true;
});

const supportedCurrencies = computed<ICurrency[]>(() => {
  const availableCurrencies = themeContext.value.availableCurrencies;
  const orderedCurrencies = ["USD", "EUR", "GBP", "SEK", "CNY", "JPY", "BRL"];

  const sortedCurrencies = availableCurrencies.sort((a, b) => {
    const indexA = orderedCurrencies.indexOf(a.code);
    const indexB = orderedCurrencies.indexOf(b.code);

    if (indexA === -1) {
      return 1;
    }
    if (indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });

  return isInrCurrencyEnabled.value ? sortedCurrencies : sortedCurrencies.filter((currency) => currency.code !== "INR");
});

// India-geolocated shoppers may only pick currencies accepted for India orders, once INR is live.
const selectableCurrencies = computed<ICurrency[]>(() => {
  const isInrAvailable = supportedCurrencies.value.some(({ code }) => code === "INR");
  if (themeContext.value.countryCode !== "IN" || !isInrAvailable) {
    return supportedCurrencies.value;
  }

  return supportedCurrencies.value.filter(({ code }) => INDIA_SUPPORTED_CURRENCIES.includes(code));
});

const contactCurrency = computed(() =>
  supportedCurrencies.value?.find((item) => item.code === user.value?.contact?.currencyCode),
);

const countryCodeTLDCurrency = computed(() => {
  if (themeContext.value.storeSettings.modules) {
    const featureFlags = new FeatureFlags(themeContext.value.storeSettings.modules);
    const ccTLDsValue = (featureFlags.getValue("Thorlabs.Commerce", "ccTLDs") as string) || "";
    const domains = ccTLDsValue ? (JSON.parse(ccTLDsValue) as Record<string, LanguageAndCurrency>) : {};
    const hostname = location.hostname;
    const strippedHostname = hostname.replace(/^www\./, "");

    return (domains[hostname] ?? domains[strippedHostname])?.Currency;
  }

  return null;
});

function getGeolocationCurrency(): string {
  const countryCode = themeContext.value.countryCode as keyof typeof countryCodeToLanguageAndCurrencyMap;

  if (countryCodeToLanguageAndCurrencyMap[countryCode]) {
    return (
      supportedCurrencies.value.find((x) => x.code === countryCodeToLanguageAndCurrencyMap[countryCode].Currency)
        ?.code || defaultCurrency.value.code
    );
  }

  return defaultCurrency.value.code;
}

const currentCurrency = computed<ICurrency>(() => {
  if (punchoutCookies && punchoutCookies.value && punchoutCookies.value.currency) {
    // At punchout authentication we store the punchout currency in the local storage which translates in savedCurrencyCode
    return supportedCurrencies.value.find((item) => item.code === savedCurrencyCode.value) || defaultCurrency.value;
  }
  if (contactCurrency?.value && contactCurrency.value?.code) {
    return supportedCurrencies.value.find((item) => item.code === contactCurrency.value?.code) || defaultCurrency.value;
  } else if (
    user.value?.contact?.currencyCode === BRL_CURRENCY_CODE &&
    isBrlCurrencyDisabled(themeContext.value.storeSettings.modules)
  ) {
    // BRL contact while BRL is disabled: skip guest/browser fallbacks.
    return defaultCurrency.value;
  } else if (savedCurrencyCode.value) {
    return supportedCurrencies.value.find((item) => item.code === savedCurrencyCode.value) || defaultCurrency.value;
  } else if (countryCodeTLDCurrency.value) {
    return (
      supportedCurrencies.value.find((item) => item.code === countryCodeTLDCurrency.value) || defaultCurrency.value
    );
  } else {
    return supportedCurrencies.value?.find((item) => item.code === getGeolocationCurrency()) || defaultCurrency.value;
  }
});

function saveCurrencyCode(code: string, needToReload: boolean = true) {
  savedCurrencyCode.value = code;
  // Also persist to localStorage so future new tabs inherit this preference as their seed.
  localStorage.setItem("currency", code);

  if (needToReload) {
    location.reload();
  }
}

export function useCurrency() {
  return {
    savedCurrencyCode,
    defaultCurrency,
    supportedCurrencies,
    selectableCurrencies,
    currentCurrency,
    isInrCurrencyEnabled,
    saveCurrencyCode,
  };
}
